import React, { useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { LayoutDashboard, Tag, Package, Layers, Upload, ArrowLeft, GitCompareArrows, ChevronsLeft, ChevronsRight } from 'lucide-react';
import './abbinatore-v2.css';

const NAV = [
  { to: '/admin/abbinatore-v2', label: 'Dashboard', icon: LayoutDashboard, exact: true },
  { to: '/admin/abbinatore-v2/macchine', label: 'Macchine', icon: Package },
  { to: '/admin/abbinatore-v2/compatibilita', label: 'Compatibilità', icon: GitCompareArrows },
  { to: '/admin/abbinatore-v2/articoli', label: 'Articoli', icon: Tag },
  { to: '/admin/abbinatore-v2/abbinamento', label: 'Abbinamento', icon: Layers },
  { to: '/admin/abbinatore-v2/import-export', label: 'Import / Export', icon: Upload },
];

const COLLAPSE_KEY = 'abbinatore-v2-sidebar-collapsed';

/**
 * Layout dell'Abbinatore v2 (sperimentale).
 *
 * Sidebar interna a 9 voci (separata dalla sidebar admin globale),
 * collassabile a sola icona (stato persistito in localStorage).
 * Le sezioni vengono renderizzate via <Outlet /> dal router.
 */
export default function AbbinatoreV2Layout() {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === '1');

  const toggleCollapsed = () => {
    setCollapsed(prev => {
      const next = !prev;
      localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0');
      return next;
    });
  };

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-white" data-testid="abbinatore-v2-layout">
      {/* Sidebar interna */}
      <aside
        className={`flex-none bg-[#fafafa] border-r border-[#e2e2df] py-5 flex flex-col transition-[width] duration-200 ${collapsed ? 'w-[60px] px-2' : 'w-56 px-3'}`}
        data-testid="abbinatore-v2-sidebar"
      >
        <div className={`mb-4 ${collapsed ? 'px-0' : 'px-2'}`}>
          <Link
            to="/admin"
            data-testid="back-to-admin"
            title="Torna al pannello admin"
            className={`inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#6d6d68] hover:text-[#2b3a8f] mb-3 transition-colors ${collapsed ? 'justify-center w-full' : ''}`}
          >
            <ArrowLeft className="w-3 h-3 flex-none" />
            {!collapsed && <span>Torna al pannello admin</span>}
          </Link>
          {!collapsed && <div className="text-base font-semibold text-[#1f1f1d]">Abbinatore</div>}
        </div>
        <nav className="flex-1 flex flex-col gap-0.5 overflow-y-auto" data-testid="abbinatore-v2-nav">
          {NAV.map(item => {
            const Icon = item.icon;
            const active = item.exact
              ? location.pathname === item.to
              : location.pathname.startsWith(item.to);
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.exact}
                title={collapsed ? item.label : undefined}
                data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                className={`flex items-center gap-2.5 px-3 py-2 rounded-md text-[15px] font-medium transition-colors ${collapsed ? 'justify-center px-0' : ''} ${
                  active
                    ? 'bg-[#eef1fb] text-[#2b3a8f]'
                    : 'text-[#5c5c57] hover:bg-[#f0f0ed]'
                }`}
              >
                <Icon className="w-3.5 h-3.5 flex-none" />
                {!collapsed && <span className="truncate">{item.label}</span>}
              </NavLink>
            );
          })}
        </nav>

        {/* Bottone comprimi/espandi in fondo alla sidebar */}
        <div className="flex-none pt-3 mt-2 border-t border-[#e2e2df]">
          <button
            onClick={toggleCollapsed}
            data-testid="abbinatore-sidebar-toggle"
            title={collapsed ? 'Espandi sidebar' : 'Comprimi sidebar'}
            className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-[13px] font-medium text-[#6d6d68] hover:bg-[#f0f0ed] hover:text-[#2b3a8f] transition-colors ${collapsed ? 'justify-center px-0' : ''}`}
          >
            {collapsed ? <ChevronsRight className="w-3.5 h-3.5 flex-none" /> : <ChevronsLeft className="w-3.5 h-3.5 flex-none" />}
            {!collapsed && <span>Comprimi sidebar</span>}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <div className="flex-1 px-8 pt-7 pb-5 bg-white min-h-0 overflow-y-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
